import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
} from "../ui/breadcrumb";
import { formatBreadcrumb } from "@/shared/utils/format-breadcrumbs";

type Crumb = { name: string; path: string; isLast: boolean };

function BreadcrumbSeparator() {
  return (
    <span className="mx-1 text-[#A2A2A2] font-inter text-lg font-medium leading-normal [font-feature-settings:'liga_off','clig_off']">
      /
    </span>
  );
}

/** Build crumbs from pathname — school/teacher/student/content-creator shortcuts replace segment expansion (no stacking). */
function buildPathCrumbs(
  pathname: string,
  segments: string[],
  t: (key: string) => string,
): Crumb[] {
  if (/^\/teachers\/edit\/[^/]+\/?$/.test(pathname)) {
    return [
      { name: t("common:breadcrumb.teachers"), path: "/teachers", isLast: false },
      { name: t("common:breadcrumb.editTeacher"), path: pathname, isLast: true },
    ];
  }

  if (/^\/teachers\/class-mapping\/?$/.test(pathname)) {
    return [
      { name: t("common:breadcrumb.teachers"), path: "/teachers", isLast: false },
      { name: t("common:breadcrumb.classMapping"), path: pathname, isLast: true },
    ];
  }

  if (/^\/students\/edit\/[^/]+\/?$/.test(pathname)) {
    return [
      { name: t("common:breadcrumb.students"), path: "/students", isLast: false },
      { name: t("common:breadcrumb.editStudent"), path: pathname, isLast: true },
    ];
  }

  if (/^\/content-creators\/edit\/[^/]+\/?$/.test(pathname)) {
    return [
      { name: t("common:breadcrumb.contentCreators"), path: "/content-creators", isLast: false },
      { name: t("common:breadcrumb.editContentCreator"), path: pathname, isLast: true },
    ];
  }

  if (/^\/tenants\/?$/.test(pathname)) {
    return [{ name: t("tenants:breadcrumb.tenants"), path: pathname, isLast: true }];
  }

  if (/^\/tenants\/add\/?$/.test(pathname)) {
    return [
      { name: t("tenants:breadcrumb.tenants"), path: "/tenants", isLast: false },
      { name: t("tenants:breadcrumb.add"), path: pathname, isLast: true },
    ];
  }

  if (/^\/tenants\/edit\/[^/]+\/?$/.test(pathname)) {
    return [
      { name: t("tenants:breadcrumb.tenants"), path: "/tenants", isLast: false },
      { name: t("tenants:breadcrumb.edit"), path: pathname, isLast: true },
    ];
  }

  if (/^\/institutions\/?$/.test(pathname)) {
    return [
      { name: t("institutions:breadcrumb.institutions"), path: pathname, isLast: true },
    ];
  }

  if (/^\/institutions\/add\/?$/.test(pathname)) {
    return [
      { name: t("institutions:breadcrumb.institutions"), path: "/institutions", isLast: false },
      { name: t("institutions:breadcrumb.add"), path: pathname, isLast: true },
    ];
  }

  if (/^\/institutions\/admins\/?$/.test(pathname)) {
    return [
      { name: t("institutions:breadcrumb.institutions"), path: "/institutions", isLast: false },
      { name: t("institutions:breadcrumb.admins"), path: pathname, isLast: true },
    ];
  }

  if (/^\/institutions\/edit\/[^/]+\/?$/.test(pathname)) {
    return [
      { name: t("institutions:breadcrumb.institutions"), path: "/institutions", isLast: false },
      { name: t("institutions:breadcrumb.edit"), path: pathname, isLast: true },
    ];
  }

  return segments.map((segment, idx) => {
    const path = "/" + segments.slice(0, idx + 1).join("/");
    const decodedSegment = decodeURIComponent(segment);
    const name = getSegmentName(decodedSegment, t);

    return { name, path, isLast: idx === segments.length - 1 };
  });
}

function getSegmentName(segment: string, t: (key: string) => string): string {
  switch (segment) {
    case "dashboard":
      return t("common:breadcrumb.dashboard");
    case "teachers":
      return t("common:breadcrumb.teachers");
    case "students":
      return t("common:breadcrumb.students");
    case "content-creators":
      return t("common:breadcrumb.contentCreators");
    case "tenants":
      return t("common:breadcrumb.tenants");
    case "institutions":
      return t("common:breadcrumb.institutions");
    case "add-teachers":
      return t("common:breadcrumb.addTeacher");
    case "add-students":
      return t("common:breadcrumb.addStudent");
    case "add-content-creators":
      return t("common:breadcrumb.addContentCreator");
    case "bulk-upload":
      return t("common:breadcrumb.bulkUpload");
    case "view-profile":
      return t("common:breadcrumb.viewProfile");
    case "courses":
      return t("common:breadcrumb.courses");
    case "lessons":
      return t("common:breadcrumb.lessons");
    case "tests":
      return t("common:breadcrumb.tests");
    case "all-tests":
      return t("common:breadcrumb.allTests");
    case "library":
      return t("common:breadcrumb.library");
    case "reports":
      return t("common:breadcrumb.reports");
    case "my-subscriptions":
      return t("common:breadcrumb.mySubscriptions");
    default:
      return formatBreadcrumb(segment);
  }
}

export function AppBreadcrumbs({ courseName = "" }: { courseName?: string }) {
  const { t } = useTranslation(["tenants", "institutions", "common"]);
  const { pathname, search } = useLocation();
  const dashboardCrumb = {
    name: t("common:breadcrumb.dashboard"),
    path: "/dashboard",
    isLast: false,
  };
  const segments = pathname.split("/").filter(Boolean);

  let crumbs = buildPathCrumbs(pathname, segments, t);

  if (courseName) {
    crumbs = crumbs.map((crumb, index) =>
      index === crumbs.length - 1 && courseName
        ? { ...crumb, name: courseName }
        : crumb,
    );
  }

  const params = new URLSearchParams(search);
  const editId = params.get("edit");
  if (editId && crumbs.length > 0) {
    const last = crumbs[crumbs.length - 1];
    if (last.name === t("common:breadcrumb.addTeacher")) {
      crumbs = crumbs.map((c, i) =>
        i === crumbs.length - 1 ? { ...c, name: t("common:breadcrumb.editTeacher") } : c,
      );
    }
    if (last.name === t("common:breadcrumb.addStudent")) {
      crumbs = crumbs.map((c, i) =>
        i === crumbs.length - 1 ? { ...c, name: t("common:breadcrumb.editStudent") } : c,
      );
    }
    if (last.name === t("common:breadcrumb.addContentCreator")) {
      crumbs = crumbs.map((c, i) =>
        i === crumbs.length - 1 ? { ...c, name: t("common:breadcrumb.editContentCreator") } : c,
      );
    }
  }

  if (crumbs.length === 1 && pathname !== "/dashboard") {
    crumbs = [
      dashboardCrumb,
      {
        ...crumbs[0],
        isLast: true,
      },
    ];
  }

  if (!crumbs.length || crumbs.length < 2) return null;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => (
          <BreadcrumbItem key={`${crumb.path}-${index}`}>
            {!crumb.isLast ? (
              <>
                <BreadcrumbLink asChild>
                  <Link
                    to={crumb.path}
                    className="text-[#A2A2A2] font-inter text-lg font-medium leading-normal [font-feature-settings:'liga_off','clig_off']"
                  >
                    {crumb.name}
                  </Link>
                </BreadcrumbLink>
                <BreadcrumbSeparator />
              </>
            ) : (
              <BreadcrumbPage className="text-[#181818] font-inter text-lg font-medium leading-normal [font-feature-settings:'liga_off','clig_off']">
                {crumb.name}
              </BreadcrumbPage>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
